import { useCallback, useEffect, useState } from 'react';
import {
  Gauge, Timer, Droplets, Check, AlertTriangle, Loader2, Info, X,
} from 'lucide-react';
import Button from '../ui/Button';
import sprayService from '../../services/spray';
import { toNum } from '@/utils/numeric';
import type { Numeric } from '@/utils/numeric';
import type {
  CalibrationMethod, CalibrationInput, CalibrationPreview,
  EquipmentCalibration, SwathSource,
} from '../../types/spray';

/**
 * Everything the wizard reads off a machine, and nothing else.
 *
 * A sprayer is a sub-type in the one equipment registry, so this takes an
 * `Equipment` record. Naming the fields rather than the whole type keeps the
 * wizard from acquiring a dependency on the registry's shape — it needs a name,
 * a type to pick a default method, and the geometry it cannot ask the operator
 * to re-measure.
 */
export interface CalibratableEquipment {
  id: number;
  name: string;
  equipment_type: string;
  operating_speed_kph?: Numeric;
  operating_pressure_bar?: Numeric;
  boom_width_m?: Numeric;
  nozzle_spacing_cm?: Numeric;
  /** Set on a weed-strip sprayer: the total width it wets per pass. */
  strip_width_m?: Numeric;
}

interface CalibrationWizardProps {
  equipment: CalibratableEquipment;
  /** Links the calibration to the spray that demanded it — this is what clears the block. */
  sprayApplicationId?: number | null;
  /** Row spacing of the spray's targets, used as the airblast swath default. */
  suggestedRowSpacingM?: number | null;
  onClose: () => void;
  onSaved: (calibration: EquipmentCalibration) => void;
}

/**
 * Which method suits this machine.
 *
 * Catching a single jet is straightforward on a boom, where the nozzles are
 * identical and reachable. On an airblast they are numerous, angled and
 * awkward, so the only honest measurement is to run a known volume out and
 * time it. The default follows the equipment type; the operator can override,
 * because a machine's type does not always describe how it is plumbed.
 */
function defaultMethodFor(equipmentType: string): CalibrationMethod {
  return equipmentType === 'boom_sprayer' ? 'nozzle' : 'tank_empty';
}

const METHODS: { key: CalibrationMethod; label: string; blurb: string }[] = [
  {
    key: 'nozzle',
    label: 'Measure one jet',
    blurb: 'Catch the output of a single nozzle for one minute at operating pressure, then count the nozzles on the boom.',
  },
  {
    key: 'tank_empty',
    label: 'Time the tank out',
    blurb: 'Put a known volume in, run at operating RPM and pressure, and time how long it takes to empty. Use this for an airblast, where catching one jet is impractical.',
  },
];

function Field({
  label, value, onChange, unit, placeholder, step = '0.01', hint,
}: {
  label: string;
  value: number | '';
  onChange: (v: number | '') => void;
  unit?: string;
  placeholder?: string;
  step?: string;
  hint?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-neutral-600 mb-1">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          step={step}
          inputMode="decimal"
          value={value}
          placeholder={placeholder}
          onChange={e => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
          className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        {unit && <span className="text-sm text-neutral-500 shrink-0">{unit}</span>}
      </div>
      {hint && <p className="text-xs text-neutral-400 mt-1">{hint}</p>}
    </div>
  );
}

/**
 * Record a sprayer calibration.
 *
 * The operator enters what they measured; the server derives the rate and
 * returns it live, so the number they are about to commit is on screen before
 * they commit it. Nothing here lets them type an output rate directly — that
 * was the old behaviour, and it is what made a calibration unfalsifiable.
 */
function CalibrationWizard({
  equipment,
  sprayApplicationId = null,
  suggestedRowSpacingM = null,
  onClose,
  onSaved,
}: CalibrationWizardProps) {
  const [method, setMethod] = useState<CalibrationMethod>(() => defaultMethodFor(equipment.equipment_type));

  // nozzle method
  const [nozzleFlow, setNozzleFlow]   = useState<number | ''>('');
  const [nozzleCount, setNozzleCount] = useState<number | ''>('');

  // tank-empty method — 500 L is the default because it is a convenient,
  // clearly-marked fraction of most tanks and long enough to time accurately.
  const [testVolume, setTestVolume]   = useState<number | ''>(500);
  const [timeMin, setTimeMin]         = useState<number | ''>('');
  const [timeSec, setTimeSec]         = useState<number | ''>('');

  // conditions
  const [speed, setSpeed]       = useState<number | ''>(toNum(equipment.operating_speed_kph) ?? '');
  const [pressure, setPressure] = useState<number | ''>(toNum(equipment.operating_pressure_bar) ?? '');
  const [rpm, setRpm]           = useState<number | ''>('');

  // swath
  const [rowSpacing, setRowSpacing] = useState<number | ''>(suggestedRowSpacingM ?? '');
  const [notes, setNotes]           = useState('');

  const [preview, setPreview] = useState<CalibrationPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // A weed-strip sprayer's swath is the strip it wets, whatever row it drives
  // down; the server uses it ahead of anything sent, and so does this.
  const stripWidth = toNum(equipment.strip_width_m) || null;
  const boomWidth = stripWidth ? null : toNum(equipment.boom_width_m);
  // A boom's swath is the machine; an airblast's is the row it drives down, so
  // only the latter needs the operator to supply one.
  // A nozzle calibration can instead take its swath from nozzle count × spacing,
  // but only when the machine has a spacing recorded.
  const nozzleSpacing = toNum(equipment.nozzle_spacing_cm);
  const fixedSwath = !!stripWidth || !!boomWidth;
  const needsRowSpacing = !fixedSwath && !(method === 'nozzle' && nozzleSpacing);

  const buildInput = useCallback((): CalibrationInput | null => {
    if (speed === '' || Number(speed) <= 0) return null;

    const base: CalibrationInput = { method, speed_kph: Number(speed) };
    if (pressure !== '') base.operating_pressure_bar = Number(pressure);
    if (rpm !== '')      base.operating_rpm = Number(rpm);
    if (notes.trim())    base.notes = notes.trim();
    if (sprayApplicationId) base.spray_application_id = sprayApplicationId;

    // The row spacing field is on screen whenever there is no boom width, so
    // whatever is typed there is sent - dropping it for the nozzle method left
    // the server with no swath and a 400 on every preview.
    if (needsRowSpacing || (!fixedSwath && rowSpacing !== '')) {
      if (rowSpacing === '' || Number(rowSpacing) <= 0) return null;
      base.reference_swath_m = Number(rowSpacing);
      base.swath_source = 'row_spacing' as SwathSource;
    }

    if (method === 'nozzle') {
      if (nozzleFlow === '' || nozzleCount === '') return null;
      if (Number(nozzleFlow) <= 0 || Number(nozzleCount) <= 0) return null;
      base.nozzle_flow_lpm = Number(nozzleFlow);
      base.nozzle_count = Number(nozzleCount);
      if (equipment.nozzle_spacing_cm != null) {
        base.nozzle_spacing_cm = toNum(equipment.nozzle_spacing_cm);
      }
    } else {
      const seconds = (timeMin === '' ? 0 : Number(timeMin) * 60) + (timeSec === '' ? 0 : Number(timeSec));
      if (testVolume === '' || Number(testVolume) <= 0 || seconds <= 0) return null;
      base.test_volume_l = Number(testVolume);
      base.time_to_empty_sec = Math.round(seconds);
    }
    return base;
  }, [
    method, speed, pressure, rpm, notes, sprayApplicationId, needsRowSpacing,
    rowSpacing, nozzleFlow, nozzleCount, testVolume, timeMin, timeSec,
    equipment.nozzle_spacing_cm, fixedSwath,
  ]);

  // Live preview. Debounced so a half-typed number does not fire a request per
  // keystroke, and cancelled on change so a slow response cannot overwrite a
  // newer one.
  useEffect(() => {
    const input = buildInput();
    if (!input) { setPreview(null); setPreviewError(null); return; }

    let cancelled = false;
    const t = setTimeout(() => {
      sprayService.previewCalibration(equipment.id, input)
        .then(res => {
          if (cancelled) return;
          if (res.success && res.data) {
            setPreview(res.data);
            setPreviewError(null);
          } else {
            setPreview(null);
            setPreviewError((res as { error?: { message?: string } }).error?.message ?? null);
          }
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          // A 400 rejects rather than resolving with success: false, and its
          // message is the only thing telling the operator which figure is wrong.
          setPreview(null);
          setPreviewError(err instanceof Error ? err.message : 'Could not calculate the rate');
        });
    }, 350);

    return () => { cancelled = true; clearTimeout(t); };
  }, [buildInput, equipment.id]);

  const save = async () => {
    const input = buildInput();
    if (!input) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await sprayService.createCalibration(equipment.id, input);
      if (res.success && res.data) onSaved(res.data);
      else setSaveError((res as { error?: { message?: string } }).error?.message || 'Could not save the calibration');
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Could not save the calibration');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-200 sticky top-0 bg-white">
          <h3 className="text-base font-semibold text-neutral-800 flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary-600" />
            Calibrate {equipment.name}
          </h3>
          <button type="button" onClick={onClose} className="text-neutral-400 hover:text-neutral-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Method */}
          <div className="space-y-2">
            {METHODS.map(m => (
              <button
                key={m.key}
                type="button"
                onClick={() => setMethod(m.key)}
                className={`w-full text-left px-3 py-2.5 rounded-lg border-2 transition-colors ${
                  method === m.key
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
              >
                <p className={`text-sm font-medium ${method === m.key ? 'text-primary-800' : 'text-neutral-800'}`}>
                  {m.label}
                </p>
                <p className="text-xs text-neutral-500 mt-0.5">{m.blurb}</p>
              </button>
            ))}
          </div>

          {/* Measurements */}
          {method === 'nozzle' ? (
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Flow from one jet"
                value={nozzleFlow}
                onChange={setNozzleFlow}
                unit="L/min"
                placeholder="0.80"
                hint="Caught over a full minute"
              />
              <Field
                label="Nozzles on the boom"
                value={nozzleCount}
                onChange={setNozzleCount}
                step="1"
                placeholder="24"
              />
            </div>
          ) : (
            <div className="space-y-3">
              <Field
                label="Water put in"
                value={testVolume}
                onChange={setTestVolume}
                unit="L"
                step="10"
              />
              <div>
                <label className="block text-xs font-medium text-neutral-600 mb-1">Time to empty</label>
                <div className="flex items-center gap-2">
                  <input
                    type="number" min="0" step="1" inputMode="numeric"
                    value={timeMin}
                    onChange={e => setTimeMin(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    placeholder="5"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    aria-label="Minutes to empty"
                  />
                  <span className="text-sm text-neutral-500">min</span>
                  <input
                    type="number" min="0" max="59" step="1" inputMode="numeric"
                    value={timeSec}
                    onChange={e => setTimeSec(e.target.value === '' ? '' : parseFloat(e.target.value))}
                    placeholder="00"
                    className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                    aria-label="Seconds to empty"
                  />
                  <span className="text-sm text-neutral-500">sec</span>
                </div>
                <p className="text-xs text-neutral-400 mt-1">At operating RPM and pressure</p>
              </div>
            </div>
          )}

          {/* Conditions */}
          <div className="grid grid-cols-3 gap-3">
            <Field label="Travel speed" value={speed} onChange={setSpeed} unit="km/h" placeholder="6.0" />
            <Field label="Pressure" value={pressure} onChange={setPressure} unit="bar" placeholder="8" />
            <Field label="RPM" value={rpm} onChange={setRpm} step="10" placeholder="540" />
          </div>
          <p className="text-xs text-neutral-500 flex items-start gap-1.5">
            <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
            The rate is only valid at this speed, pressure and RPM. Change gear and it changes.
          </p>

          {/* Swath */}
          {stripWidth ? (
            <p className="text-xs text-neutral-500">
              Sprays a weed strip <strong>{stripWidth} m</strong> wide per pass, from the equipment
              record. The rate is litres per hectare of strip actually sprayed, not of the block.
            </p>
          ) : boomWidth ? (
            <p className="text-xs text-neutral-500">
              Swath: <strong>{boomWidth} m</strong> boom width, from the equipment record.
            </p>
          ) : (
            <Field
              label="Row spacing"
              value={rowSpacing}
              onChange={setRowSpacing}
              unit="m"
              placeholder="4.5"
              hint={
                suggestedRowSpacingM
                  ? `Suggested ${suggestedRowSpacingM} m from this spray's blocks. For an airblast the row is the swath, so the rate is re-derived per block anyway.`
                  : "For an airblast the row is the swath. This one is the reference; each block's own spacing is used when the spray is costed."
              }
            />
          )}

          {/* Live result */}
          {preview && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-3 space-y-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-neutral-500">Total flow</p>
                  <p className="font-semibold text-neutral-800 flex items-center gap-1">
                    <Droplets className="h-4 w-4 text-green-600" />
                    {preview.total_flow_lpm} L/min
                  </p>
                </div>
                <div>
                  <p className="text-xs text-neutral-500">Application rate</p>
                  <p className="font-semibold text-green-800 text-base">
                    {preview.output_rate_lpha ?? '—'} L/ha
                    {preview.swath_source === 'strip' && (
                      <span className="block text-xs font-normal text-neutral-500">per sprayed hectare</span>
                    )}
                  </p>
                </div>
                {preview.hectares_per_tank != null && (
                  <div>
                    <p className="text-xs text-neutral-500">One tank covers</p>
                    <p className="font-semibold text-neutral-800">
                      {preview.hectares_per_tank} ha{preview.swath_source === 'strip' && ' of strip'}
                    </p>
                  </div>
                )}
                {preview.minutes_per_tank != null && (
                  <div>
                    <p className="text-xs text-neutral-500">One tank lasts</p>
                    <p className="font-semibold text-neutral-800 flex items-center gap-1">
                      <Timer className="h-4 w-4 text-neutral-400" />
                      {preview.minutes_per_tank} min
                    </p>
                  </div>
                )}
              </div>
              <p className="text-xs text-neutral-500">
                {preview.swath_source === 'strip' ? 'Strip' : 'Swath'} {preview.reference_swath_m} m at {preview.speed_kph} km/h
              </p>
            </div>
          )}

          {previewError && (
            <p className="text-xs text-amber-600 flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              {previewError}
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-neutral-600 mb-1">Notes</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Nozzles replaced, filter cleaned…"
              className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm resize-none"
            />
          </div>

          {saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <div className="flex items-center gap-2 px-5 py-4 border-t border-neutral-200 sticky bottom-0 bg-white">
          <Button variant="primary" onClick={save} disabled={saving || !preview}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            Save calibration
          </Button>
          <button type="button" onClick={onClose} className="text-sm text-neutral-500 hover:text-neutral-700">
            Cancel
          </button>
          {!preview && (
            <span className="text-xs text-neutral-400 ml-auto text-right">
              Fill in the measurements to see the rate
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default CalibrationWizard;
